'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { X } from 'lucide-react';
import { api } from '@/lib/api';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@/app/stores/useUserStore';
import { UserProfile } from '@/lib/api';
import InviteBg from '@/app/assets/image/invite/bg.svg';
import CoDigitInput from '@/components/co-digit-input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

interface InviteDialogProps {
  title?: React.ReactNode;
  open: boolean;
  manualBind?: boolean;
  allowSkip?: boolean;
  isExpired?: boolean;
  /** 是否跳过绑定，只有登录页是false，其他所有页面都是true */
  isSkipBind?: boolean;
  /** 待审核/已拒绝等状态下禁止再次提交人工申请 */
  applicationSubmitLocked?: boolean;
  isLoading?: boolean;
  containerRef?: HTMLElement | null;
  onOpenChange?: (open: boolean) => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  onExpiredConfirm?: (code: string, reason: string) => void;
}

export default function InviteDialog({
  title,
  open,
  manualBind = false,
  allowSkip = true,
  isExpired = false,
  applicationSubmitLocked = false,
  isLoading = false,
  isSkipBind = true,
  containerRef = undefined,
  onConfirm,
  onOpenChange,
  onCancel,
  onExpiredConfirm,
}: InviteDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [inviteCodeDigits, setInviteCodeDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [inviteCodeErrorMsg, setInviteCodeErrorMsg] = useState('');
  const [inviteReason, setInviteReason] = useState('');
  const [isBinding, setIsBinding] = useState(false);
  // 更新store信息
  const { setUserInfo, userInfo } = useUserStore();
  const handleConfirm = async (code: string, skip = false) => {
    // 如果过期的话，不自动提交申请，需要手动提交
    if (isExpired || applicationSubmitLocked) {
      return;
    }
    !skip && setIsBinding(true);
    try {
      const response = await api.user.bindInviteCode(code, isSkipBind);
      if (response.code === 200) {
        !skip && message.success(t('rewards.inviteUserDesc'));
        setUserInfo({
          ...(userInfo as UserProfile),
          inviter: {
            inviterCode: code,
            userId: response.data.inviterUserId,
            username: response.data.inviterUserName,
            avatar: response.data.inviterAvatar,
          },
        });
        onConfirm && onConfirm();
      } else {
        const errorMessage = locale === 'zh-CN' ? response.msg : response.enMsg || response.msg;
        !skip && setInviteCodeErrorMsg(errorMessage || t('error.systemErrorDesc'));
      }
    } catch (error) {
      !skip && setInviteCodeErrorMsg(t('error.systemErrorDesc'));
    } finally {
      !skip && setIsBinding(false);
    }
  };
  // 暂不绑定，直接进入
  const handleCancel = () => {
    if (manualBind) {
      handleConfirm('VIP888', true);
    }
    if (onCancel) {
      onCancel();
    }
  };
  useEffect(() => {
    if (open) {
      setInviteCodeDigits(['', '', '', '', '', '']);
      setInviteCodeErrorMsg('');
      setIsBinding(false);
      setInviteReason('');
    }
  }, [open]);

  const handleClose = () => {
    if (manualBind) {
      handleConfirm('VIP888', true);
      return;
    }
    onOpenChange?.(false);
  };


  return (
    <Modal
      open={open}
      footer={null}
      width={450}
      centered
      closable={false}
      onCancel={handleClose}
      destroyOnHidden
      classNames={{ container: '!p-0' }}
      getContainer={() => containerRef || document.body}
    >
      <div className="p-5">
        <div className="flex items-center gap-2 text-xl font-bold text-font-secondary justify-end hover:text-font-primary/100">
          <X className="size-5 cursor-pointer" onClick={handleClose} />
        </div>
        {title ?? null}
        <div className="pt-7 relative flex flex-col gap-6">
          <div className="flex flex-col text-[22px] font-bold text-font-primary">
            <span>{t('invite.dialog.bindTitle')}</span>
            <span>{t('invite.dialog.getRewards')}</span>
          </div>
          <div className="absolute -top-1 right-2 w-[130px] h-[130px]">
            <img src={InviteBg} alt="invite" className="w-full h-full object-cover" />
          </div>
          <div className="w-full -mb-2">
            <CoDigitInput
              type="alphanumeric"
              autoFocus={open}
              disabled={isBinding || applicationSubmitLocked}
              value={inviteCodeDigits}
              onChange={setInviteCodeDigits}
              onComplete={handleConfirm}
              errorMsg={inviteCodeErrorMsg}
              onErrorMsgChange={setInviteCodeErrorMsg}
              idPrefix="invite-digit"
            />
            {isExpired && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="invite-code-error-msg">申请原因</Label>
                <Textarea
                  className="resize-none"
                  disabled={applicationSubmitLocked}
                  value={inviteReason}
                  placeholder="请输入申请原因，不能超过200字"
                  onChange={e => setInviteReason(e.target.value)}
                />
              </div>
            )}
          </div>
          {applicationSubmitLocked && <p className="text-sm text-orange-500 text-center">当前不可再次提交申请，请留意顶部审核状态提示。</p>}
          {isExpired && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="secondary" className="w-full" disabled={isLoading} onClick={() => onOpenChange?.(false)}>
                取消申请
              </Button>
              <Button
                className="w-full"
                disabled={applicationSubmitLocked || isLoading}
                loading={isLoading}
                onClick={() => onExpiredConfirm?.(inviteCodeDigits.join(''), inviteReason)}
              >
                确认
              </Button>
            </div>
          )}
          <div className="flex flex-col items-center gap-2 justify-center">
            {allowSkip && (
              <div
                className="text-base text-font-primary/50 text-center cursor-pointer transition-all duration-300 hover:text-font-primary underline-offset-4 hover:underline"
                onClick={handleCancel}
              >
                {t('invite.dialog.skipAndEnter')}
              </div>
            )}
            <div className="text-xs text-orange-500 text-center">
              {t('invite.dialog.noCodePrefix')}
              <span
                className="ml-0.5 underline decoration-dotted cursor-pointer underline-offset-4 hover:decoration-solid"
                onClick={() => window.open('https://t.me/billionlive_global', '_blank')}
              >
                {t('invite.dialog.getFromCommunity')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
